import React, { useState } from 'react';
import { Box, Typography, Container, Card, CardContent, styled, Grid, Alert, Snackbar, TablePagination, Divider, useTheme, CircularProgress } from '@mui/material';
import { Upload, PersonAdd } from '@mui/icons-material';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Key from '../clientVariables.json';
import Page from '../components/Page';
import ContactTable from '../components/contacts/ContactTable';
import Button from '../components/common/Button';
import AddContactModal from '../components/contacts/AddContactModal';
import SendJobPanel from '../components/contacts/SendJobPanel';
import { useContactContext } from '../contexts/ContactContext';
import { useJobContext } from '../contexts/JobContext';
import ContactSearchBar from '../components/contacts/ContactSearchBar';

const ContactCrm = () => {
  const theme = useTheme();  const {
    contacts,
    filteredContacts,
    setFilteredContacts,
    selectedContacts,
    toggleContactSelection,
    selectAllContacts,
    addContact,
    addContacts,
    selectedJob,
    setSelectedJob,
    emailTemplate,
    setEmailTemplate,
    sendJob,
    pagination,
    handlePageChange,
    handleChangeRowsPerPage,
    getPaginatedContacts,
    isLoading,
    error,
    fetchContacts,
    searchFilters,
    setSearch,
    deleteContacts
  } = useContactContext();
  
  const { jobs } = useJobContext();
  const validJobs = jobs.filter(job => job.status === 'valid');
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [showSendJobPanel, setShowSendJobPanel] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');  const [isUploadLoading, setIsUploadLoading] = useState(false);
  
  const handleFilterChange = (filters) => {
    // Update the search filters - only client-side filtering for now
    let filtered = contacts;
    
    if (filters.jobTitle) {
      const title = filters.jobTitle.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.jobTitle.toLowerCase().includes(title)
      );
    }
    
    if (filters.state) {
      filtered = filtered.filter(contact => contact.location.state === filters.state);
      
      if (filters.city) {
        filtered = filtered.filter(contact => contact.location.city === filters.city);
      }
    }
    
    setFilteredContacts(filtered);
    
    // Reset pagination to first page when filtering
    handlePageChange(0);
  };
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear any previous errors
    setFileUploadError('');

    // Check file extension
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      setFileUploadError('Please upload a CSV or Excel file.');
      return;
    }

      // Validate file type
    if (!file.name.endsWith('.csv')) {
      setFileUploadError('Please upload a CSV file. Excel (.xlsx) format is not supported currently.');
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setFileUploadError('File is too large. Please limit CSV files to 5MB or less.');
      return;
    }

    // Parse the CSV file
    Papa.parse(file, {
      header: true, // Assuming first row is headers
      skipEmptyLines: true,
      complete: async (results) => {
        // Check if data is valid
        if (results.errors.length > 0) {
          setFileUploadError(`CSV parsing error: ${results.errors[0].message}`);
          return;
        }

        if (results.data.length === 0) {
          setFileUploadError('The CSV file appears to be empty.');
          return;
        }
          // Process the data - extract contacts
        try {
          const extractedContacts = processContactsFromCsv(results.data);

          // Check if any contacts were extracted
          if (extractedContacts.length === 0) {
            setFileUploadError('No valid contacts found in CSV. Please ensure your CSV has name, email, and jobTitle columns.');
            return;
          }

          // Show loading state
          setIsUploadLoading(true);

          // Get existing contact emails to check for duplicates
          const existingEmails = new Set(
            contacts.map(contact => contact.email.toLowerCase().trim()).filter(email => email)
          );

          // Filter out duplicates within CSV and against existing contacts
          const { uniqueContacts, csvDuplicates, dbDuplicates, duplicateCount, csvDuplicateCount } = filterDuplicateContacts(extractedContacts, existingEmails);

          // Show duplicate information if any found
          if (csvDuplicates.length > 0 || dbDuplicates.length > 0) {
            let duplicateMessage = 'Found duplicate contacts:\n\n';

            if (dbDuplicates.length > 0) {
              duplicateMessage += 'Already in database:\n';
              dbDuplicates.forEach(dup => {
                duplicateMessage += `• ${dup.name} (${dup.email})\n`;
              });
              duplicateMessage += '\n';
            }

            if (csvDuplicates.length > 0) {
              duplicateMessage += 'Duplicates within CSV:\n';
              csvDuplicates.forEach(dup => {
                duplicateMessage += `• ${dup.name} (${dup.email})\n`;
              });
              duplicateMessage += '\n';
            }

            if (uniqueContacts.length > 0) {
              duplicateMessage += `Proceeding to add ${uniqueContacts.length} unique contact${uniqueContacts.length > 1 ? 's' : ''}.`;
            } else {
              duplicateMessage += 'No new contacts to add.';
            }

            // Show the duplicate information as a warning
            console.warn(duplicateMessage);

            // If no unique contacts, show error and return
            if (uniqueContacts.length === 0) {
              setFileUploadError(duplicateMessage.replace(/\n/g, ' '));
              setIsUploadLoading(false);
              return;
            }
          }

          // Directly call the add-contactCRM API for each unique contact
          Promise.all(uniqueContacts.map(contact => {
            return axios.post(`${Key.domain}/common/add-contactCRM`, {
              name: contact.name || '',
              email: contact.email,
              city: contact.location?.city || '',
              state: contact.location?.state || '',
              country: contact.location?.country || 'United States',
              tags: contact.tags?.join(',') || '',
            });
          }))
          .then(() => {
            // After all contacts are added, refresh the contacts list
            fetchContacts();

            // Show detailed success message with duplicate information
            let message = `Successfully imported ${uniqueContacts.length} new contact${uniqueContacts.length > 1 ? 's' : ''}.`;

            if (duplicateCount > 0 || csvDuplicateCount > 0) {
              const totalSkipped = duplicateCount + csvDuplicateCount;
              message += `\n\nSkipped ${totalSkipped} duplicate${totalSkipped > 1 ? 's' : ''}:`;

              if (dbDuplicates.length > 0) {
                message += `\n\nAlready in database (${dbDuplicates.length}):`;
                dbDuplicates.slice(0, 5).forEach(dup => {
                  message += `\n• ${dup.name} (${dup.email})`;
                });
                if (dbDuplicates.length > 5) {
                  message += `\n• ... and ${dbDuplicates.length - 5} more`;
                }
              }

              if (csvDuplicates.length > 0) {
                message += `\n\nDuplicates within CSV (${csvDuplicates.length}):`;
                csvDuplicates.slice(0, 5).forEach(dup => {
                  message += `\n• ${dup.name} (${dup.email})`;
                });
                if (csvDuplicates.length > 5) {
                  message += `\n• ... and ${csvDuplicates.length - 5} more`;
                }
              }
            }
            setSuccessMessage(message);
            setShowSuccessAlert(true);
          })
          .catch(error => {
            console.error("Error adding contacts:", error);
            setFileUploadError(`Error adding contacts: ${error.message}`);
          })
          .finally(() => {
            setIsUploadLoading(false);
          });
        } catch (error) {
          setFileUploadError(`Error processing contacts: ${error.message}`);
        }
      },
      error: (error) => {
        setFileUploadError(`Error reading CSV file: ${error.message}`);
      }
    });
  };
  
  // Helper function to process contacts from CSV data
  const extractedContacts = [];
  const processContactsFromCsv = (csvData) => {
    
    // Process each row of the CSV
    csvData.forEach((row) => {
      // Check for required fields (name, email)
      if (!row.name && !row.Name && !row['Full Name']) {
        return; // Skip rows without a name
      }
      
      // Extract data with fallbacks for different possible column names
      const name = row.name || row.Name || row['Full Name'] || '';
      const email = row.email || row.Email || row['Email Address'] || '';
      const jobTitle = row.jobTitle;
      const state = row.state || row.State || '';
      const city = row.city || row.City || '';
      
      // Basic validation
      if (!name.trim()) {
        return; // Skip if name is just whitespace
      }
      
      // Process tags if available (assuming comma-separated)
      let tags = [];
      if (row.tags || row.Tags) {
        const tagString = row.tags || row.Tags;
        tags = tagString.split(',').map(tag => tag.trim()).filter(tag => tag);
      }
      
      // Add industry as tag if available
      if (row.industry || row.Industry) {
        const industry = (row.industry || row.Industry).trim();
        if (industry && !tags.includes(industry)) {
          tags.push(industry);
        }
      }
      
      // Create the contact object
      const contactObj = {
        name,
        email,
        jobTitle,
        location: {
          state,
          city
        },
        tags
      };
      
      extractedContacts.push(contactObj);
    });
    
    return extractedContacts;
  };

  // Helper function to filter out duplicate contacts
  const filterDuplicateContacts = (extractedContacts, existingEmails) => {
    const seenEmails = new Set();
    const uniqueContacts = [];
    const csvDuplicates = [];
    const dbDuplicates = [];

    extractedContacts.forEach(contact => {
      if (!contact.email || !contact.email.trim()) {
        // Skip contacts without email
        return;
      }

      const normalizedEmail = contact.email.toLowerCase().trim();

      // Check if email already exists in database
      if (existingEmails.has(normalizedEmail)) {
        dbDuplicates.push({
          name: contact.name,
          email: contact.email,
          reason: 'Already exists in database'
        });
        return;
      }

      // Check if email is duplicate within CSV
      if (seenEmails.has(normalizedEmail)) {
        csvDuplicates.push({
          name: contact.name,
          email: contact.email,
          reason: 'Duplicate within CSV file'
        });
        return;
      }

      // Email is unique, add to results
      seenEmails.add(normalizedEmail);
      uniqueContacts.push(contact);
    });

    return {
      uniqueContacts,
      csvDuplicates,
      dbDuplicates,
      duplicateCount: dbDuplicates.length,
      csvDuplicateCount: csvDuplicates.length
    };
  };

  const handleSendJobComplete = async () => {
    const count = await sendJob();
    return count;
  };

  const HiddenInput = styled('input')({
    display: 'none',
  });

  return (
    <Page title="Contact CRM">
       <Grid sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mb: 3 }}>
          {/* <Box>
            <Typography variant="h4" gutterBottom>
              Contact CRM
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage contacts and send job opportunities.
            </Typography>
          </Box> */}       
           <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>              <Button
                component="label"
                variant="outlined"
                startIcon={isUploadLoading ? <CircularProgress size={16} /> : <Upload />}
                htmlFor="file-upload"
                disabled={isUploadLoading}
              >
                {isUploadLoading ? 'Uploading...' : 'Upload Contacts'}
                <HiddenInput
                  type="file"
                  id="file-upload"
                  accept=".csv"
                  onChange={handleFileUpload}
                  disabled={isUploadLoading}
                />
              </Button>
            </Box>
            <Button
              onClick={() => setIsAddContactModalOpen(true)}
              startIcon={<PersonAdd />}
            >              Add Contact
            </Button>
            <Button
              variant="text"
              component="a"
              href="/static/sample_contacts_template.csv"
              download="contact_import_template.csv"
              size="small"
              sx={{ ml: 1 }}
            >
              Download Template
            </Button>
          </Box>
        </Box>
        
        {fileUploadError && (
          <Card sx={{ mb: 3, bgcolor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
            <CardContent>
              <Typography color="error.main">
                {fileUploadError}
              </Typography>
            </CardContent>
          </Card>        )}
          {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}        <Card sx={{ mb: 3 }}>
          <CardContent>
            <ContactSearchBar />
          </CardContent>
        </Card>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>        ) : (
          <ContactTable
            contacts={getPaginatedContacts()}
            onToggleSelection={toggleContactSelection}
            onSelectAll={selectAllContacts}
          />
        )}
        
        {/* Pagination Component */}
        <Card sx={{ mt: 2, p: 1, boxShadow: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center', 
            justifyContent: 'space-between',
            px: 2,
            py: 1
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: { xs: 2, md: 0 }
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 1, color: theme.palette.primary.main }}>
                Total Records: 
              </Typography>              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {pagination.totalCount}
              </Typography>
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 2 }} />
            
            <TablePagination
              component="div"
              count={pagination.totalCount}
              page={pagination.page}
              onPageChange={(_, newPage) => handlePageChange(newPage)}
              rowsPerPage={pagination.rowsPerPage}
              onRowsPerPageChange={(event) => handleChangeRowsPerPage(parseInt(event.target.value, 10))}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Contacts per page:"
              sx={{
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-input': {
                  fontSize: '14px',
                  fontWeight: 'medium',
                  color: theme.palette.text.primary,
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  gap: 1,
                },
                '& .MuiButtonBase-root': {
                  backgroundColor: theme.palette.background.neutral || '#f5f5f5',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover || '#e0e0e0',
                  }
                }
              }}
            />
          </Box>
        </Card>
        
        {selectedContacts.length > 0 && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 3,
              border: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minWidth: '300px',
              zIndex: 1000
            }}
          >
            <Typography variant="body2" fontWeight="medium" color="text.secondary" mr={3}>
              {selectedContacts.length} contacts selected
            </Typography>
            <Button size="small" onClick={() => setShowSendJobPanel(true)}>
              Send Job
            </Button>
          </Box>
        )}        <AddContactModal
          isOpen={isAddContactModalOpen}
          onClose={() => setIsAddContactModalOpen(false)}
        />
          <SendJobPanel
          selectedContacts={showSendJobPanel ? selectedContacts : []} // Only pass contacts when panel should be visible
          validJobs={validJobs}
          selectedJob={selectedJob}
          onSelectJob={setSelectedJob}
          emailSubject={emailTemplate.subject}
          onUpdateEmailSubject={(subject) => setEmailTemplate({ ...emailTemplate, subject })}
          emailBody={emailTemplate.body}
          onUpdateEmailBody={(body) => setEmailTemplate({ ...emailTemplate, body })}
          onSendJob={handleSendJobComplete}
          onClose={() => {
            setShowSendJobPanel(false);
            setSelectedJob(null);
          }}
        />
        
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={10000}
          onClose={() => setShowSuccessAlert(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccessAlert(false)}
            severity="success"
            variant="filled"
            sx={{
              width: '100%',
              maxWidth: '600px',
              '& .MuiAlert-message': {
                whiteSpace: 'pre-line',
                fontSize: '14px',
                lineHeight: 1.4
              }
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </Page>
  );
};

export default ContactCrm;
